import type { ObserverGesture, ObserverMotion } from '../observerContext';

/*
  Курьер ядра (spec-3-motion §9): граница с чатом «жидкого помощника».

  Наше — план дома, маршрут, проверки, профиль скорости и длительность, точки посадки, нормали, место
  (room), мениск и кольцо, уход и приём дома, следы, пересборка и прерывания. Их — как выглядит капля в
  пути, как она отвечает на скорость, кривизну и нормаль, оседание и разворот в тело, жесты и темп
  реакций, сила деформации. Тело подключается только через CourierBody и проп провайдера body.

  Слои курьера (fixed inset-0, pointer-events-none, aria-hidden, z-fab): root — наш (translate по пути),
  attach — наш (поправка на прокрутку и привязка к стене), tilt и skin — тела, contact — наш SVG рядом.
*/

/** Улица дома, по которой идёт капля. */
export type Lane = 'hall' | 'sill' | 'roof' | 'wall' | 'shelf' | 'glide' | 'gate';

export interface PathSample {
  /** Мс от старта. */
  t: number;
  /** Центр капли в экранных px. */
  x: number;
  y: number;
  /** Курс по касательной, рад. */
  heading: number;
  /** Скорость и ускорение вдоль хода: px/мс и px/мс². */
  speed: number;
  accel: number;
  /** Кривизна со знаком, 1/px. */
  curvature: number;
  lane: Lane;
  /** Нормаль от поверхности, вдоль которой идёт; null на воротах и дугах между улицами. */
  normal: [number, number] | null;
  /** Свободный поперечник, px. */
  room: number;
}

export interface Track {
  samples: PathSample[];
  duration: number;
  length: number;
  at(t: number): PathSample;
}

export interface Contact {
  kind: 'block' | 'tab' | 'menu' | 'side' | 'zone';
  /** Центр тела в экранных px. */
  x: number;
  y: number;
  /** Наружу от кромки. */
  normal: [number, number];
  /** Наибольшее тело, которое помещается. */
  size: 26 | 16 | 8;
  room: number;
  /** Скорость за 40 px до касания / крейсерская, 0..1. */
  impact: number;
  anchor: HTMLElement;
}

/** Финал тела у цели на холсте (EVENT_PLAN.finale): одна волна от камня или разлив «готово». */
export type CourierFinale = 'wave' | 'success';

/**
 * Тело курьера. Договор: в кадр передачи тело — круглая капля 8 px с центром в начале координат root,
 * цвет var(--core-tone); позицию не пишет (root и attach — не его); поперечник в пути не больше
 * min(8, room); у цели не больше Contact.size и не выходит из room; длительность пути не меняет;
 * react() продлевает удержание не больше чем на 800 мс; при уменьшенном движении не монтируется.
 */
export interface CourierBody {
  mount(tilt: HTMLElement, skin: HTMLElement): void;
  /** Кадры формы на весь путь — offset как у root, тот же startTime. */
  travel(track: Track, motion: ObserverMotion): { tilt?: Keyframe[]; skin?: Keyframe[] };
  /** Если телу нужна живая кинематика. */
  onFrame?(s: PathSample): void;
  /** Оседание и разворот, не дольше 420 мс. */
  land(c: Contact): Promise<void>;
  /** Жест у цели; finale — финал тела на холсте из EVENT_PLAN (тело вправе им не пользоваться). */
  react(g: ObserverGesture, c: Contact, finale?: CourierFinale | null): Promise<void>;
  /** Не дольше 200 мс, итог — круглая капля 8 px. */
  lift(c: Contact): Promise<void>;
  /** Стенд «Замедлить ×4»: во сколько раз медленнее играть свои движения. */
  pace?(k: number): void;
  unmount(): void;
}

/* ----- План дома (plan.ts читает его из разметки одним пакетом; route.ts и kinematics.ts — чистые) ----- */

/** Прямоугольник в экранных px — простые числа: дальше план DOM не трогает. */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Карточка рамки: главное меню, показанный уровень 2–3, окно. */
export interface ShellCard {
  kind: 'menu' | 'zone' | 'pane' | 'detail';
  box: Box;
  /** Скругление верхнего правого угла: дуга вокруг соседа концентрична ему. */
  radius: number;
}

export interface PanePlan {
  box: Box;
  /** Полоса вкладок и видимая часть её прокрутки (список вкладок). */
  strip: Box;
  tabs: Box;
  /** Видимая часть прокрутки показанной вкладки ([data-scrollport]). */
  scroll: Box;
}

export interface HousePlan {
  /** Потолок: низ верхней полосы стенда; в основном сайте — край экрана. */
  ceiling: number;
  menu: {
    box: Box;
    /** Ось коридора (левое поле меню) и порога (между строкой знака и первым пунктом). */
    hallX: number;
    sillY: number;
    /** Коридор тянется от верха меню + 24 до строки «я» − 8. */
    hallTop: number;
    hallBottom: number;
    /** Видимая часть прокрутки пунктов. */
    scroll: Box;
    items: { id: string; box: Box }[];
    /** Ворота «порог»: справа от меню сразу окно (главная). */
    sillGate: boolean;
  };
  /** Карточки рамки слева направо. */
  cards: ShellCard[];
  panes: PanePlan[];
  /** Где ядро дома: центр сферы в знаке или значка выделения. */
  home: { kind: 'mark' | 'pill'; x: number; y: number };
}

/** Цель на плане: пункт меню, пункт меню приложения, блок (или строка в нём), строка без блока, вкладка. */
export type TargetGeom =
  | { kind: 'menu'; id: string }
  | { kind: 'zone'; box: Box }
  | { kind: 'panel'; pane: number; box: Box }
  | { kind: 'block'; pane: number; box: Box }
  | { kind: 'row'; pane: number; box: Box }
  | { kind: 'tab'; pane: number; box: Box };

/**
 * Цель — пункт меню (главного или приложения): капля прислоняется к пункту, тело не разворачивается,
 * мениска и колец нет (на пунктах меню колец нет — spec-3 §6).
 */
export const dropOnly = (kind: Contact['kind']) => kind === 'menu' || kind === 'zone';

/** Точка посадки: центр тела, нормаль наружу от кромки, высота полки подхода. */
export interface SeatGeom {
  kind: Contact['kind'];
  /** Правая панель: посадка каплей на верхнюю кромку, подход с крыши. */
  panel?: boolean;
  x: number;
  y: number;
  normal: [number, number];
  /** Окно цели; −1 — меню. */
  pane: number;
  /** Полка подхода (блок, сбоку); у вкладки — крыша, у меню — коридор. */
  shelfY: number;
}

/** Точка пути в плотной выборке (~1 px): из них режутся и обращаются рёбра графа. */
export interface PathPoint {
  x: number;
  y: number;
  /** Курс, рад (без скачков на ±π внутри пути). */
  h: number;
  /** Кривизна со знаком, 1/px. */
  k: number;
  lane: Lane;
  normal: [number, number] | null;
  room: number;
  /**
   * Привязка к рамке для слоя attach: 0 — рамка (крыша, коридор, ворота), 1 — окно (стена: сдвиг окна по x),
   * 2 — цель (полка, спуск, посадка: сдвиг якоря). Дробные — переход на дугах между ними.
   */
  pin: number;
}
